import { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";

interface RevokeModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (revokeReason?: string) => void;
    title?: string;
    description?: string;
    loading?: boolean;
}

export default function RevokeModal({
    open,
    onClose,
    onConfirm,
    title = "Revoke Coupon",
    description = "Are you sure you want to revoke this coupon? This action cannot be undone.",
    loading = false,
}: RevokeModalProps) {
    const [revokeReason, setRevokeReason] = useState("");

    const handleConfirm = () => {
        if (!revokeReason.trim() || revokeReason.trim().length < 10) {
            return; // Don't proceed if no reason provided or too short
        }
        onConfirm(revokeReason.trim());
        setRevokeReason("");
    };

    const handleClose = () => {
        setRevokeReason("");
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h6" color="error.main">
                    {title}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {description}
                </Typography>

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Reason"
                    placeholder="Enter reason..."
                    value={revokeReason}
                    onChange={(e) => setRevokeReason(e.target.value)}
                    variant="outlined"
                    size="small"
                    required
                    helperText={`Minimum ${10 - revokeReason.trim().length} characters required`}
                    error={revokeReason.trim().length > 0 && revokeReason.trim().length < 10}
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} variant="outlined" disabled={loading}>
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleConfirm}
                    variant="contained"
                    color="error"
                    loading={loading}
                    disabled={loading || !revokeReason.trim() || revokeReason.trim().length < 10}
                >
                    Revoke
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
